import { createFileRoute, Outlet, Navigate, useRouterState } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { AppShell } from "@/components/app-shell";
import { useRealtimeCalls } from "@/hooks/use-realtime";
import { useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_app")({
  component: AppLayout,
});

function AppLayout() {
  const { user, loading, role, clientId, effectiveClientId, signOut, profileError, refresh } = useAuth();
  // A client user can only read their own tenant, so only subscribe once we
  // know who they are and which dealership they belong to.
  // The layout can re-render once more while the router is already moving to
  // /auth; never take /auth itself as the place to return to (that nested
  // ?redirect= inside ?redirect= on every render).
  const here = useRouterState({ select: (st) => (st.location.pathname.startsWith("/auth") ? null : st.location.href) });
  const canRead = !!user && !loading && (role === "admin" || !!clientId);
  useRealtimeCalls(effectiveClientId ?? null, role, canRead);
  useSmartfloSync(canRead);
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  // Remember where they were going so sign-in can bring them back (deep links).
  if (!user) return here ? <Navigate to="/auth" search={{ redirect: here } as never} replace /> : null;
  if (profileError) {
    return (
      <div className="min-h-screen grid place-items-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 space-y-3 text-center">
            <h1 className="text-lg font-semibold">Couldn't load your account</h1>
            <p className="text-sm text-muted-foreground">
              We couldn't reach the server to check your access. Check your connection and try again.
            </p>
            <div className="flex justify-center gap-2">
              <Button onClick={() => refresh()}>Try again</Button>
              <Button variant="outline" onClick={() => signOut()}>Sign out</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (role !== "admin" && !clientId) {
    return (
      <div className="min-h-screen grid place-items-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 space-y-3 text-center">
            <h1 className="text-lg font-semibold">Account pending</h1>
            <p className="text-sm text-muted-foreground">
              Your account ({user.email}) doesn't have access yet. An administrator can give it
              access in Admin → Everyone with access.
            </p>
            <Button variant="outline" onClick={() => signOut()}>Sign out</Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  return <AppShell><Outlet /></AppShell>;
}

/**
 * While the panel is open, ask the server to catch up with Smartflo's call
 * records (fills in any call whose webhook never arrived). The server runs it
 * at most every few minutes however many people are looking; new rows reach
 * the page through the realtime listeners.
 */
function useSmartfloSync(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    const ping = () => { fetch("/api/sync-tick?source=app", { method: "POST" }).catch(() => {}); };
    ping();
    const t = setInterval(ping, 5 * 60_000);
    return () => clearInterval(t);
  }, [enabled]);
}
